import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faStar,
    faGraduationCap,
    faBookOpen,
    faVideo,
    faEye,
    faArrowLeft,
    faGift,
    faCertificate,
    faThLarge,
    faSlidersH,
    faRotateRight,
    faTimes,
    faFire,
    faArrowDownWideShort,
    faArrowUpWideShort,
    faClock,
    faLayerGroup,
    faFilter,
    faMoneyBillWave,
    faChevronDown,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../Services/ApiServices';
import Pagination from '../Shared/Pagination';
import Loader from '../Shared/Loader';
import Header from '../Shared/Header';
import Footer from '../Shared/Footer';
import {
    Dropdown,
    DropdownItem,
    DropdownLabel,
    DropdownDivider
} from '../Shared/Dropdown/Dropdown';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import { encryptId } from '../../utils/encryption';
import htmlHelper from '../../Helpers/htmlHelper';
import { toastHelper } from '../../Helpers/toastHelper';

// Animation variants
const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
    }),
};

// CourseSortOption Enum mapping
export const CourseSortOption = {
    MostPopular: 1,    // الأكثر رواجاً
    PriceHighToLow: 2, // السعر: من الأعلى إلى الأقل
    PriceLowToHigh: 3, // السعر: من الأقل إلى الأعلى
    Newest: 4,         // جديدنا
    TopRated: 5,       // الأفضل تقييماً
};

const SORT_OPTIONS = [
    { value: CourseSortOption.MostPopular, label: 'الأكثر رواجاً', icon: faFire },
    { value: CourseSortOption.Newest, label: 'أحدث الدورات (جديدنا)', icon: faClock },
    { value: CourseSortOption.TopRated, label: 'الأفضل تقييماً', icon: faStar },
    { value: CourseSortOption.PriceLowToHigh, label: 'السعر: من الأقل للأعلى', icon: faArrowDownWideShort },
    { value: CourseSortOption.PriceHighToLow, label: 'السعر: من الأعلى للأقل', icon: faArrowUpWideShort },
];

const PRICE_PRESETS = [
    { label: 'الكل', min: null, max: null },
    { label: 'مجاناً فقط', min: 0, max: 0 },
    { label: 'أقل من 100 ج.م', min: null, max: 100 },
    { label: '100 - 300 ج.م', min: 100, max: 300 },
    { label: 'أكثر من 300 ج.م', min: 300, max: null },
];

const RATING_OPTIONS = [
    { value: '', label: 'جميع التقييمات' },
    { value: '4.5', label: '4.5+ نجوم' },
    { value: '4', label: '4+ نجوم' },
    { value: '3', label: '3+ نجوم' },
];

const WebsiteCourses = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Data state
    const [courses, setCourses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });

    // Filter states
    const [searchInput, setSearchInput] = useState(searchParams.get('Word') || '');
    const [searchQuery, setSearchQuery] = useState(searchParams.get('Word') || '');
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('CategoryId') || '');
    const [sortOption, setSortOption] = useState(
        searchParams.get('SortOption') ? Number(searchParams.get('SortOption')) : CourseSortOption.MostPopular
    );
    const [minPrice, setMinPrice] = useState(searchParams.get('MinPrice') || '');
    const [maxPrice, setMaxPrice] = useState(searchParams.get('MaxPrice') || '');
    const [minRate, setMinRate] = useState(searchParams.get('MinRate') || '');
    const [pageSize, setPageSize] = useState(Number(searchParams.get('PageSize')) || 9);
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get('PageNumber')) || 1);

    // Mobile / Advanced Filter Drawer Toggle
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);

    // Fetch Categories Lookup
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setCategoriesLoading(true);
                const response = await ApiServices.get('Lookup/Categories');
                if (Array.isArray(response)) {
                    setCategories(response);
                } else if (response?.data && Array.isArray(response.data)) {
                    setCategories(response.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, []);

    // Fetch Courses with parameters
    const fetchCourses = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('PageSize', pageSize);
            params.append('PageNumber', page);
            params.append('IsActive', 'true');

            if (searchQuery.trim()) {
                params.append('Word', searchQuery.trim());
            }
            if (selectedCategory && selectedCategory !== 'all') {
                params.append('CategoryId', selectedCategory);
            }
            if (sortOption) {
                params.append('SortOption', sortOption);
            }
            if (minPrice !== '' && minPrice !== null && !isNaN(minPrice)) {
                params.append('MinPrice', minPrice);
            }
            if (maxPrice !== '' && maxPrice !== null && !isNaN(maxPrice)) {
                params.append('MaxPrice', maxPrice);
            }
            if (minRate !== '' && minRate !== null && Number(minRate) > 0) {
                params.append('MinRate', minRate);
            }

            const url = `Home/Courses/getAll?${params.toString()}`;
            const response = await ApiServices.get(url);

            if (response && response.data) {
                setCourses(response.data.items || []);
                setTotalRecords(response.data.total_records || 0);
                setPagination({
                    currentPage: response.data.current_page || response.data.page_number || page,
                    totalPages: response.data.total_pages || 1,
                });
            } else {
                setCourses([]);
                setTotalRecords(0);
                setPagination({ currentPage: 1, totalPages: 1 });
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
            toastHelper.error('حدث خطأ أثناء تحميل بيانات الدورات التعليمية');
            setCourses([]);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedCategory, sortOption, minPrice, maxPrice, minRate, pageSize]);

    // Re-fetch when filters change
    useEffect(() => {
        setCurrentPage(1);
        fetchCourses(1);
    }, [searchQuery, selectedCategory, sortOption, minPrice, maxPrice, minRate, pageSize, fetchCourses]);

    // Handle pagination change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchCourses(page);
        window.scrollTo({ top: 380, behavior: 'smooth' });
    };

    // Handle search form submission (click button or press Enter)
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setSearchQuery(searchInput.trim());
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchInput('');
        setSearchQuery('');
    };

    // Reset all filters
    const handleResetFilters = () => {
        setSearchInput('');
        setSearchQuery('');
        setSelectedCategory('');
        setSortOption(CourseSortOption.MostPopular);
        setMinPrice('');
        setMaxPrice('');
        setMinRate('');
        setCurrentPage(1);
    };

    // Check if any filter is active
    const isFiltered = Boolean(
        searchQuery ||
        (selectedCategory && selectedCategory !== 'all') ||
        sortOption !== CourseSortOption.MostPopular ||
        minPrice !== '' ||
        maxPrice !== '' ||
        (minRate !== '' && Number(minRate) > 0)
    );

    // Find current category name
    const activeCategoryObj = categories.find((c) => String(c.id) === String(selectedCategory));
    const currentSortLabel = SORT_OPTIONS.find((s) => s.value === sortOption)?.label || 'الأكثر رواجاً';
    const currentRateLabel = RATING_OPTIONS.find((r) => r.value === String(minRate))?.label || 'جميع التقييمات';

    return (
        <div className="wbc-root" dir="rtl">
            <Header />

            {/* ── Hero Section ── */}
            <section className="wbc-hero">
                <div className="wbc-container">
                    <motion.div
                        className="wbc-header"
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                    >
                        <motion.div className="wbc-eyebrow" variants={fadeUp} custom={0}>
                            <span className="wbc-eyebrow-dot" />
                            مسارات تعليمية متكاملة
                        </motion.div>

                        <motion.h1 className="wbc-title" variants={fadeUp} custom={1}>
                            اكتشف أفضل <em>الدورات التعليمية</em>
                        </motion.h1>

                        <motion.p className="wbc-subtitle" variants={fadeUp} custom={2}>
                            تصفح باقة من أقوى الدورات التفاعلية والمسارات التدريبية الشاملة بأعلى معايير الجودة من نخبة المعلمين.
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* ── Main Catalog Area ── */}
            <div className="wbc-container wbc-main-layout">
                {/* ── Primary Controls (Search, Category, PageSize) above Toolbar ── */}
                <motion.div
                    className="wbc-hero-controls"
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    custom={3}
                >
                    {/* Search Form with Submit Button */}
                    <form className="wbc-search-box" onSubmit={handleSearchSubmit}>
                        <FontAwesomeIcon icon={faSearch} className="wbc-search-icon" />
                        <input
                            type="text"
                            placeholder="ابحث عن دورة تعليمية، مادة، أو كلمة..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                        {searchInput && (
                            <button
                                type="button"
                                className="wbc-search-clear"
                                onClick={handleClearSearch}
                                title="مسح البحث"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                        <button type="submit" className="wbc-search-submit-btn" title="بدء البحث">
                            <FontAwesomeIcon icon={faSearch} />
                            <span>بحث</span>
                        </button>
                    </form>

                    {/* Category Dropdown using Shared Dropdown Component */}
                    <div className="wbc-hero-dropdown-item">
                        <Dropdown
                            align="right"
                            width="auto"
                            className="wbc-dropdown-full"
                            trigger={
                                <div className="wbc-dropdown-trigger-hero">
                                    <div className="wbc-trigger-content">
                                        <FontAwesomeIcon icon={faLayerGroup} className="wbc-trigger-icon" />
                                        <span className="wbc-trigger-label">التصنيف:</span>
                                        <span className="wbc-trigger-val">
                                            {activeCategoryObj ? activeCategoryObj.name : 'جميع التصنيفات'}
                                        </span>
                                    </div>
                                    <FontAwesomeIcon icon={faChevronDown} className="wbc-trigger-arrow" />
                                </div>
                            }
                        >
                            <DropdownLabel>اختر التصنيف التعليمي</DropdownLabel>
                            <DropdownItem
                                icon={<FontAwesomeIcon icon={faLayerGroup} />}
                                trailingIcon={!selectedCategory ? <FontAwesomeIcon icon={faCheck} color="#6366f1" /> : null}
                                onClick={() => setSelectedCategory('')}
                            >
                                جميع التصنيفات
                            </DropdownItem>
                            <DropdownDivider />
                            {categories.map((cat) => (
                                <DropdownItem
                                    key={cat.id}
                                    trailingIcon={String(selectedCategory) === String(cat.id) ? <FontAwesomeIcon icon={faCheck} color="#6366f1" /> : null}
                                    onClick={() => setSelectedCategory(String(cat.id))}
                                >
                                    {cat.name}
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>

                    {/* Page Size Dropdown using Shared Dropdown Component */}
                    <div className="wbc-hero-dropdown-item is-compact">
                        <Dropdown
                            align="left"
                            width="auto"
                            className="wbc-dropdown-full"
                            trigger={
                                <div className="wbc-dropdown-trigger-hero">
                                    <div className="wbc-trigger-content">
                                        <FontAwesomeIcon icon={faThLarge} className="wbc-trigger-icon" />
                                        <span className="wbc-trigger-label">عرض:</span>
                                        <span className="wbc-trigger-val">{pageSize}</span>
                                    </div>
                                    <FontAwesomeIcon icon={faChevronDown} className="wbc-trigger-arrow" />
                                </div>
                            }
                        >
                            <DropdownLabel>عدد الدورات بالصفحة</DropdownLabel>
                            {[6, 9, 12, 24, 48].map((s) => (
                                <DropdownItem
                                    key={s}
                                    trailingIcon={pageSize === s ? <FontAwesomeIcon icon={faCheck} color="#6366f1" /> : null}
                                    onClick={() => setPageSize(s)}
                                >
                                    {s} دورات
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>
                </motion.div>

                {/* ── Secondary Controls Toolbar ── */}
                <div className="wbc-toolbar">
                    <div className="wbc-toolbar-left">
                        <div className="wbc-results-count">
                            {loading ? (
                                <span>جاري البحث في الدورات...</span>
                            ) : (
                                <span>
                                    تم العثور على <strong>{totalRecords}</strong> دورة تعليمية
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="wbc-toolbar-right">
                        {/* Sort Selector with Shared Dropdown */}
                        <div className="wbc-toolbar-dropdown-wrap">
                            <Dropdown
                                align="right"
                                width="auto"
                                className="wbc-dropdown-full"
                                trigger={
                                    <div className="wbc-dropdown-trigger-toolbar">
                                        <div className="wbc-trigger-content">
                                            <FontAwesomeIcon icon={faSlidersH} className="wbc-trigger-icon" />
                                            <span className="wbc-trigger-label">الترتيب:</span>
                                            <span className="wbc-trigger-val">{currentSortLabel}</span>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronDown} className="wbc-trigger-arrow" />
                                    </div>
                                }
                            >
                                <DropdownLabel>ترتيب الدورات حسب</DropdownLabel>
                                {SORT_OPTIONS.map((opt) => (
                                    <DropdownItem
                                        key={opt.value}
                                        icon={<FontAwesomeIcon icon={opt.icon} />}
                                        trailingIcon={sortOption === opt.value ? <FontAwesomeIcon icon={faCheck} color="#6366f1" /> : null}
                                        onClick={() => setSortOption(opt.value)}
                                    >
                                        {opt.label}
                                    </DropdownItem>
                                ))}
                            </Dropdown>
                        </div>

                        {/* Rating Filter with Shared Dropdown */}
                        <div className="wbc-toolbar-dropdown-wrap">
                            <Dropdown
                                align="right"
                                width="auto"
                                className="wbc-dropdown-full"
                                trigger={
                                    <div className="wbc-dropdown-trigger-toolbar">
                                        <div className="wbc-trigger-content">
                                            <FontAwesomeIcon icon={faStar} className="wbc-trigger-icon" />
                                            <span className="wbc-trigger-label">التقييم:</span>
                                            <span className="wbc-trigger-val">{currentRateLabel}</span>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronDown} className="wbc-trigger-arrow" />
                                    </div>
                                }
                            >
                                <DropdownLabel>تصفية حسب التقييم</DropdownLabel>
                                {RATING_OPTIONS.map((opt) => (
                                    <DropdownItem
                                        key={opt.value}
                                        icon={opt.value ? <FontAwesomeIcon icon={faStar} color="#f59e0b" /> : null}
                                        trailingIcon={String(minRate) === opt.value ? <FontAwesomeIcon icon={faCheck} color="#6366f1" /> : null}
                                        onClick={() => setMinRate(opt.value)}
                                    >
                                        {opt.label}
                                    </DropdownItem>
                                ))}
                            </Dropdown>
                        </div>

                        {/* Price Filter Toggle Button */}
                        <button
                            type="button"
                            className={`wbc-btn-filter-toggle ${minPrice !== '' || maxPrice !== '' ? 'has-active' : ''}`}
                            onClick={() => setShowFilterDrawer(!showFilterDrawer)}
                        >
                            <FontAwesomeIcon icon={faFilter} />
                            <span>تصفية الأسعار</span>
                            {(minPrice !== '' || maxPrice !== '') && <span className="wbc-filter-indicator" />}
                        </button>
                    </div>
                </div>

                {/* ── Collapsible Price Filter Panel ── */}
                <AnimatePresence>
                    {showFilterDrawer && (
                        <motion.div
                            className="wbc-filter-panel"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="wbc-filter-panel-inner">
                                <div className="wbc-filter-block">
                                    <h4>
                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                        <span>خيارات السعر المسبقة:</span>
                                    </h4>
                                    <div className="wbc-price-chips">
                                        {PRICE_PRESETS.map((preset, idx) => {
                                            const isSelected =
                                                String(minPrice) === String(preset.min === null ? '' : preset.min) &&
                                                String(maxPrice) === String(preset.max === null ? '' : preset.max);
                                            return (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    className={`wbc-price-chip ${isSelected ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setMinPrice(preset.min === null ? '' : preset.min);
                                                        setMaxPrice(preset.max === null ? '' : preset.max);
                                                    }}
                                                >
                                                    {preset.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="wbc-filter-block">
                                    <h4>
                                        <FontAwesomeIcon icon={faSlidersH} />
                                        <span>نطاق السعر المخصص (ج.م):</span>
                                    </h4>
                                    <div className="wbc-custom-price-inputs">
                                        <div className="wbc-price-input-group">
                                            <label className="wbc-price-input-label">من السعر:</label>
                                            <div className="wbc-price-input-wrapper">
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    value={minPrice}
                                                    onChange={(e) => setMinPrice(e.target.value)}
                                                    min="0"
                                                />
                                                <span className="wbc-price-input-unit">ج.م</span>
                                            </div>
                                        </div>

                                        <span className="wbc-price-separator">إلى</span>

                                        <div className="wbc-price-input-group">
                                            <label className="wbc-price-input-label">إلى السعر:</label>
                                            <div className="wbc-price-input-wrapper">
                                                <input
                                                    type="number"
                                                    placeholder="بلا حد أقصى"
                                                    value={maxPrice}
                                                    onChange={(e) => setMaxPrice(e.target.value)}
                                                    min="0"
                                                />
                                                <span className="wbc-price-input-unit">ج.م</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="wbc-filter-actions">
                                    <button
                                        type="button"
                                        className="wbc-btn-reset"
                                        onClick={handleResetFilters}
                                    >
                                        <FontAwesomeIcon icon={faRotateRight} />
                                        <span>إعادة تعيين الكل</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="wbc-btn-close-filter"
                                        onClick={() => setShowFilterDrawer(false)}
                                    >
                                        إغلاق
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Active Filters Summary Tags ── */}
                {isFiltered && (
                    <div className="wbc-active-tags-row">
                        <span className="wbc-active-lbl">الفلاتر المطبقة:</span>
                        <div className="wbc-active-tags">
                            {searchQuery && (
                                <span className="wbc-tag">
                                    بحث: "{searchQuery}"
                                    <button onClick={handleClearSearch}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </span>
                            )}
                            {activeCategoryObj && (
                                <span className="wbc-tag">
                                    تصنيف: {activeCategoryObj.name}
                                    <button onClick={() => setSelectedCategory('')}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </span>
                            )}
                            {(minPrice !== '' || maxPrice !== '') && (
                                <span className="wbc-tag">
                                    السعر: {minPrice !== '' ? minPrice : '0'} - {maxPrice !== '' ? maxPrice : '∞'} ج.م
                                    <button
                                        onClick={() => {
                                            setMinPrice('');
                                            setMaxPrice('');
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </span>
                            )}
                            {minRate !== '' && Number(minRate) > 0 && (
                                <span className="wbc-tag">
                                    تقييم: {minRate}+ نجوم
                                    <button onClick={() => setMinRate('')}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </span>
                            )}
                            {sortOption !== CourseSortOption.MostPopular && (
                                <span className="wbc-tag">
                                    ترتيب: {SORT_OPTIONS.find((s) => s.value === sortOption)?.label}
                                    <button onClick={() => setSortOption(CourseSortOption.MostPopular)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </span>
                            )}

                            <button className="wbc-tag-reset" onClick={handleResetFilters}>
                                مسح جميع الفلاتر
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Courses Grid / Content ── */}
                <div className="wbc-grid-container">
                    {loading ? (
                        <div className="wbc-loader-wrap">
                            <Loader />
                        </div>
                    ) : courses.length > 0 ? (
                        <>
                            <div className="wbc-grid">
                                {courses.map((course, index) => {
                                    const isFree = Number(course.price) === 0;
                                    const hasDiscount = Number(course.discount) > 0;
                                    const finalPrice = Number(course.price);
                                    const originalPrice = hasDiscount ? finalPrice + Number(course.discount) : null;
                                    const courseDetailLink = `/course/${encryptId(course.id)}`;

                                    return (
                                        <motion.div
                                            key={course.id}
                                            className="wbc-card"
                                            variants={fadeUp}
                                            initial="hidden"
                                            animate="visible"
                                            custom={index % 6}
                                            whileHover={{ y: -6 }}
                                        >
                                            {/* Course Cover & Top Badges */}
                                            <div className="wbc-card-media">
                                                {isFree ? (
                                                    <div className="wbc-badge-free">
                                                        <FontAwesomeIcon icon={faGift} />
                                                        <span>دورة مجانية</span>
                                                    </div>
                                                ) : hasDiscount ? (
                                                    <div className="wbc-badge-discount">
                                                        <span>خصم {course.discount} ج.م</span>
                                                    </div>
                                                ) : null}

                                                {course.is_certified && (
                                                    <div className="wbc-badge-certified" title="شهادة إتمام معتمدة">
                                                        <FontAwesomeIcon icon={faCertificate} />
                                                        <span>معتمدة</span>
                                                    </div>
                                                )}

                                                <Link to={courseDetailLink} className="wbc-media-link">
                                                    <img
                                                        src={getFullFileUrl(course.course_cover)}
                                                        alt={course.name}
                                                        className="wbc-card-img"
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=800&auto=format&fit=crop';
                                                        }}
                                                    />
                                                </Link>

                                                {course.category && (
                                                    <div className="wbc-cat-tag">
                                                        {course.category}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Course Body */}
                                            <div className="wbc-card-body">
                                                {/* Author & Rating */}
                                                <div className="wbc-meta-top">
                                                    <div className="wbc-instructor">
                                                        <FontAwesomeIcon icon={faGraduationCap} />
                                                        <span>{course.instructor || 'منصة امتحاني'}</span>
                                                    </div>
                                                    <div className="wbc-rate-box">
                                                        <FontAwesomeIcon icon={faStar} />
                                                        <span>{Number(course.rate || 0).toFixed(1)}</span>
                                                    </div>
                                                </div>

                                                {/* Title */}
                                                <Link to={courseDetailLink} className="wbc-title-link">
                                                    <h3 className="wbc-course-name" title={course.name}>
                                                        {course.name}
                                                    </h3>
                                                </Link>

                                                {/* Stats Badges - Consistent RTL Icon and Data alignment */}
                                                <div className="wbc-stats-row">
                                                    {Number(course.hours) > 0 && (
                                                        <div className="wbc-stat-badge" title="مدة الدورة">
                                                            <FontAwesomeIcon icon={faVideo} />
                                                            <span>
                                                                <bdi>{course.hours}</bdi> ساعة
                                                            </span>
                                                        </div>
                                                    )}
                                                    {course.material && (
                                                        <div className="wbc-stat-badge is-mat" title="المادة التعليمية">
                                                            <FontAwesomeIcon icon={faBookOpen} />
                                                            <span>{course.material}</span>
                                                        </div>
                                                    )}
                                                    <div className="wbc-stat-badge is-views" title="عدد المشاهدات">
                                                        <FontAwesomeIcon icon={faEye} />
                                                        <span>
                                                            <bdi>{course.views || 0}</bdi> مشاهدة
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Card Footer: Price & Action */}
                                                <div className="wbc-card-footer">
                                                    <div className="wbc-price-wrap">
                                                        {isFree ? (
                                                            <span className="wbc-free-pill">مجاناً</span>
                                                        ) : (
                                                            <div className="wbc-price-val-box">
                                                                <span className="wbc-price-amount">{finalPrice}</span>
                                                                <span className="wbc-price-curr">ج.م</span>
                                                                {originalPrice && (
                                                                    <span className="wbc-old-price">
                                                                        {originalPrice} ج.م
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <Link to={courseDetailLink} className="wbc-btn-view">
                                                        <span>عرض الدورة</span>
                                                        <FontAwesomeIcon icon={faArrowLeft} />
                                                    </Link>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* Pagination */}
                            <div className="wbc-pagination-wrap">
                                <Pagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </>
                    ) : (
                        /* Empty State */
                        <div className="wbc-empty-state">
                            <div className="wbc-empty-icon">
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <h3>عذراً، لم نجد أي دورات مطابقة للبحث أو الفلاتر</h3>
                            <p>جرب البحث بكلمات مختلفة أو قم بإعادة ضبط معايير الفلترة لاستكشاف جميع الدورات.</p>
                            <button
                                type="button"
                                className="wbc-btn-reset-empty"
                                onClick={handleResetFilters}
                            >
                                <FontAwesomeIcon icon={faRotateRight} />
                                <span>إعادة ضبط جميع الفلاتر</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <Footer />

            <style jsx>{`
                .wbc-root {
                    font-family: "Zain", sans-serif;
                    background: #f8fafc;
                    min-height: 100vh;
                    direction: rtl;
                    color: #0f172a;
                    overflow-x: hidden;
                    width: 100%;
                }

                .wbc-container {
                    max-width: 1320px;
                    margin: 0 auto;
                    padding: 0 24px;
                    width: 100%;
                    box-sizing: border-box;
                }

                /* ── Hero Section ── */
                .wbc-hero {
                    padding: 150px 0 40px;
                    background: linear-gradient(180deg, #f1f5f9 0%, #f8fafc 100%);
                    border-bottom: 1px solid #e2e8f0;
                    text-align: center;
                }

                .wbc-header {
                    max-width: 1000px;
                    margin: 0 auto;
                }

                .wbc-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    color: #6366f1;
                    font-weight: 800;
                    font-size: 0.95rem;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    margin-bottom: 20px;
                    background: #eef2ff;
                    padding: 8px 18px;
                    border-radius: 50px;
                    border: 1px solid #e0e7ff;
                }

                .wbc-eyebrow-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #6366f1;
                    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7);
                    animation: wbc-pulse 2s infinite cubic-bezier(0.66, 0, 0, 1);
                }

                @keyframes wbc-pulse {
                    to {
                        box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
                    }
                }

                .wbc-title {
                    font-size: clamp(2rem, 4.5vw, 3.8rem);
                    font-weight: 900;
                    color: #0f172a;
                    margin: 0 0 16px;
                    line-height: 1.22;
                    letter-spacing: -0.02em;
                }

                .wbc-title em {
                    font-style: normal;
                    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .wbc-subtitle {
                    font-size: 1.2rem;
                    color: #64748b;
                    line-height: 1.8;
                    margin: 0 auto;
                    max-width: 680px;
                    font-weight: 500;
                }

                /* ── Controls Bar (Search + Dropdown) directly above Toolbar ── */
                .wbc-hero-controls {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    width: 100%;
                    margin-bottom: 20px;
                    box-sizing: border-box;
                }

                .wbc-search-box {
                    flex: 1.6;
                    position: relative;
                    min-width: 0;
                    display: flex;
                    align-items: center;
                    margin: 0;
                    width: 100%;
                }

                .wbc-search-icon {
                    position: absolute;
                    right: 22px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #6366f1;
                    font-size: 1.15rem;
                    pointer-events: none;
                }

                .wbc-search-box input {
                    width: 100%;
                    height: 58px;
                    padding: 0 54px 0 120px;
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 50px;
                    font-family: inherit;
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #1e293b;
                    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
                    outline: none;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }

                .wbc-search-box input:focus {
                    border-color: #6366f1;
                    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.12);
                }

                .wbc-search-clear {
                    position: absolute;
                    left: 96px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    cursor: pointer;
                    font-size: 0.8rem;
                    transition: all 0.2s ease;
                    z-index: 2;
                }

                .wbc-search-clear:hover {
                    background: #fee2e2;
                    color: #ef4444;
                }

                .wbc-search-submit-btn {
                    position: absolute;
                    left: 7px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    height: 44px;
                    padding: 0 20px;
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: #ffffff;
                    border: none;
                    border-radius: 50px;
                    font-family: inherit;
                    font-weight: 800;
                    font-size: 0.95rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.28);
                    transition: all 0.25s ease;
                    z-index: 2;
                }

                .wbc-search-submit-btn:hover {
                    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
                    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.38);
                    transform: translateY(-50%) scale(1.02);
                }

                .wbc-hero-dropdown-item {
                    display: inline-flex;
                    flex: 1;
                    min-width: 180px;
                }

                .wbc-hero-dropdown-item.is-compact {
                    flex: 0.5;
                    min-width: 140px;
                }

                .wbc-dropdown-trigger-hero {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    height: 58px;
                    padding: 0 20px;
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 50px;
                    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
                    box-sizing: border-box;
                    white-space: nowrap;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    user-select: none;
                    width: 100%;
                }

                .wbc-dropdown-trigger-hero:hover {
                    border-color: #6366f1;
                    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.09);
                }

                .wbc-trigger-content {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .wbc-trigger-icon {
                    color: #6366f1;
                    font-size: 1.05rem;
                }

                .wbc-trigger-label {
                    color: #64748b;
                    font-weight: 800;
                    font-size: 0.92rem;
                }

                .wbc-trigger-val {
                    color: #0f172a;
                    font-weight: 900;
                    font-size: 1rem;
                    max-width: 140px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .wbc-trigger-arrow {
                    color: #94a3b8;
                    font-size: 0.85rem;
                    transition: transform 0.2s ease;
                }

                /* ── Main Catalog Layout ── */
                .wbc-main-layout {
                    padding: 40px 24px 100px;
                }

                /* ── Toolbar ── */
                .wbc-toolbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                    background: #ffffff;
                    padding: 16px 24px;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.03);
                    margin-bottom: 24px;
                }

                .wbc-results-count {
                    font-weight: 800;
                    font-size: 1.1rem;
                    color: #334155;
                }

                .wbc-results-count strong {
                    color: #6366f1;
                    font-weight: 900;
                }

                .wbc-toolbar-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .wbc-toolbar-dropdown-wrap {
                    display: inline-flex;
                }

                /* ── Shared Dropdown Fullwidth Helper ── */
                :global(.wbc-dropdown-full) {
                    width: 100% !important;
                    display: block !important;
                }
                :global(.wbc-dropdown-full > div) {
                    width: 100% !important;
                }

                /* ── Shared Dropdown Triggers in Toolbar ── */
                .wbc-dropdown-trigger-toolbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    height: 46px;
                    padding: 0 16px;
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    box-sizing: border-box;
                    white-space: nowrap;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    user-select: none;
                }

                .wbc-dropdown-trigger-toolbar:hover {
                    border-color: #6366f1;
                    background: #f1f5f9;
                }

                .wbc-btn-filter-toggle {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    height: 46px;
                    padding: 0 18px;
                    border-radius: 12px;
                    font-family: inherit;
                    font-weight: 800;
                    font-size: 0.95rem;
                    color: #1e293b;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    box-sizing: border-box;
                    white-space: nowrap;
                }

                .wbc-btn-filter-toggle:hover,
                .wbc-btn-filter-toggle.has-active {
                    background: #eef2ff;
                    border-color: #6366f1;
                    color: #6366f1;
                }

                .wbc-filter-indicator {
                    width: 8px;
                    height: 8px;
                    background: #6366f1;
                    border-radius: 50%;
                }

                /* ── Filter Panel ── */
                .wbc-filter-panel {
                    overflow: hidden;
                    margin-bottom: 24px;
                }

                .wbc-filter-panel-inner {
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 24px;
                    padding: 24px 28px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
                }

                .wbc-filter-block h4 {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 1.05rem;
                    font-weight: 800;
                    color: #1e293b;
                    margin: 0 0 12px;
                }

                .wbc-filter-block h4 svg {
                    color: #6366f1;
                }

                .wbc-price-chips {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }

                .wbc-price-chip {
                    padding: 8px 18px;
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 50px;
                    font-family: inherit;
                    font-weight: 700;
                    font-size: 0.92rem;
                    color: #475569;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .wbc-price-chip:hover {
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                }

                .wbc-price-chip.active {
                    background: #6366f1;
                    color: #ffffff;
                    border-color: #6366f1;
                }

                /* ── Custom Price Inputs (Responsive Form) ── */
                .wbc-custom-price-inputs {
                    display: flex;
                    align-items: flex-end;
                    gap: 14px;
                    max-width: 520px;
                    width: 100%;
                    box-sizing: border-box;
                }

                .wbc-price-input-group {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    min-width: 0;
                }

                .wbc-price-input-label {
                    font-size: 0.88rem;
                    font-weight: 800;
                    color: #64748b;
                }

                .wbc-price-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .wbc-price-input-wrapper input {
                    width: 100%;
                    height: 48px;
                    padding: 0 16px 0 46px; /* RTL: left space for unit 'ج.م' */
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    font-family: inherit;
                    font-size: 1rem;
                    font-weight: 800;
                    color: #0f172a;
                    outline: none;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }

                .wbc-price-input-wrapper input:focus {
                    border-color: #6366f1;
                    background: #ffffff;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
                }

                .wbc-price-input-unit {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-weight: 800;
                    font-size: 0.88rem;
                    pointer-events: none;
                }

                .wbc-price-separator {
                    font-weight: 900;
                    color: #94a3b8;
                    font-size: 0.95rem;
                    padding-bottom: 12px;
                    flex-shrink: 0;
                }

                .wbc-filter-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 12px;
                    border-top: 1px solid #f1f5f9;
                    padding-top: 16px;
                }

                .wbc-btn-reset {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #fee2e2;
                    color: #ef4444;
                    border: none;
                    padding: 8px 18px;
                    border-radius: 10px;
                    font-family: inherit;
                    font-weight: 800;
                    font-size: 0.92rem;
                    cursor: pointer;
                    transition: 0.2s;
                }

                .wbc-btn-reset:hover {
                    background: #fecaca;
                }

                .wbc-btn-close-filter {
                    background: #f1f5f9;
                    color: #475569;
                    border: none;
                    padding: 8px 20px;
                    border-radius: 10px;
                    font-family: inherit;
                    font-weight: 800;
                    font-size: 0.92rem;
                    cursor: pointer;
                }

                /* ── Active Tags Row ── */
                .wbc-active-tags-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    margin-bottom: 24px;
                    background: #ffffff;
                    padding: 12px 20px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                }

                .wbc-active-lbl {
                    font-weight: 800;
                    font-size: 0.92rem;
                    color: #64748b;
                }

                .wbc-active-tags {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .wbc-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #eef2ff;
                    color: #4f46e5;
                    padding: 5px 12px;
                    border-radius: 50px;
                    font-weight: 800;
                    font-size: 0.88rem;
                    border: 1px solid #e0e7ff;
                }

                .wbc-tag button {
                    background: none;
                    border: none;
                    color: #818cf8;
                    cursor: pointer;
                    padding: 0;
                    display: flex;
                    align-items: center;
                }

                .wbc-tag button:hover {
                    color: #ef4444;
                }

                .wbc-tag-reset {
                    background: none;
                    border: none;
                    color: #ef4444;
                    font-family: inherit;
                    font-weight: 800;
                    font-size: 0.88rem;
                    cursor: pointer;
                    text-decoration: underline;
                    margin-right: 8px;
                }

                /* ── Courses Grid ── */
                .wbc-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
                    gap: 32px;
                }

                /* ── Card Styling ── */
                .wbc-card {
                    background: #ffffff;
                    border-radius: 28px;
                    border: 1.5px solid rgba(15, 23, 42, 0.07);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.03);
                    transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
                }

                .wbc-card:hover {
                    border-color: rgba(99, 102, 241, 0.35);
                    box-shadow: 0 20px 40px rgba(99, 102, 241, 0.09);
                }

                .wbc-card-media {
                    position: relative;
                    height: 230px;
                    overflow: hidden;
                    background: #f1f5f9;
                }

                .wbc-media-link {
                    display: block;
                    width: 100%;
                    height: 100%;
                }

                .wbc-card-img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.5s ease;
                }

                .wbc-card:hover .wbc-card-img {
                    transform: scale(1.06);
                }

                /* Badges on image */
                .wbc-badge-free {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: #ffffff;
                    padding: 6px 14px;
                    border-radius: 12px;
                    font-weight: 900;
                    font-size: 0.88rem;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
                    z-index: 10;
                }

                .wbc-badge-discount {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    color: #ffffff;
                    padding: 6px 14px;
                    border-radius: 12px;
                    font-weight: 900;
                    font-size: 0.88rem;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
                    z-index: 10;
                }

                .wbc-badge-certified {
                    position: absolute;
                    top: 16px;
                    left: 16px;
                    background: rgba(15, 23, 42, 0.85);
                    backdrop-filter: blur(8px);
                    color: #fbbf24;
                    padding: 6px 12px;
                    border-radius: 12px;
                    font-weight: 900;
                    font-size: 0.82rem;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    z-index: 10;
                    border: 1px solid rgba(251, 191, 36, 0.4);
                }

                .wbc-cat-tag {
                    position: absolute;
                    bottom: 14px;
                    right: 16px;
                    background: rgba(255, 255, 255, 0.94);
                    backdrop-filter: blur(8px);
                    color: #334155;
                    padding: 5px 14px;
                    border-radius: 10px;
                    font-weight: 800;
                    font-size: 0.82rem;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    z-index: 5;
                }

                /* Body */
                .wbc-card-body {
                    padding: 24px;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }

                .wbc-meta-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 14px;
                }

                .wbc-instructor {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    color: #64748b;
                    font-weight: 800;
                    font-size: 0.92rem;
                    direction: rtl;
                }

                .wbc-instructor svg {
                    color: #6366f1;
                    font-size: 0.95rem;
                }

                .wbc-rate-box {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: #fef3c7;
                    color: #d97706;
                    padding: 4px 10px;
                    border-radius: 8px;
                    font-weight: 900;
                    font-size: 0.88rem;
                    direction: rtl;
                }

                .wbc-rate-box svg {
                    color: #f59e0b;
                    font-size: 0.85rem;
                }

                .wbc-title-link {
                    text-decoration: none;
                    color: inherit;
                }

                .wbc-course-name {
                    font-size: 1.35rem;
                    font-weight: 900;
                    color: #0f172a;
                    line-height: 1.4;
                    margin: 0 0 14px;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    transition: color 0.2s ease;
                }

                .wbc-card:hover .wbc-course-name {
                    color: #6366f1;
                }

                /* Stats Badges */
                .wbc-stats-row {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin-bottom: 22px;
                    padding-bottom: 18px;
                    border-bottom: 1px solid #f1f5f9;
                    direction: rtl;
                }

                .wbc-stat-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    background: #f1f5f9;
                    color: #475569;
                    border-radius: 8px;
                    font-weight: 800;
                    font-size: 0.82rem;
                    line-height: 1;
                    direction: rtl;
                    white-space: nowrap;
                }

                .wbc-stat-badge svg {
                    font-size: 0.85rem;
                    flex-shrink: 0;
                }

                .wbc-stat-badge span {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    direction: rtl;
                    unicode-bidi: isolate;
                }

                .wbc-stat-badge bdi {
                    direction: ltr;
                    unicode-bidi: isolate;
                }

                .wbc-stat-badge.is-mat {
                    background: #ecfdf5;
                    color: #059669;
                }

                .wbc-stat-badge.is-mat svg {
                    color: #059669;
                }

                .wbc-stat-badge.is-views {
                    background: #f5f3ff;
                    color: #7c3aed;
                }

                .wbc-stat-badge.is-views svg {
                    color: #7c3aed;
                }

                /* Card Footer */
                .wbc-card-footer {
                    margin-top: auto;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .wbc-price-wrap {
                    display: flex;
                    align-items: center;
                }

                .wbc-free-pill {
                    background: #ecfdf5;
                    color: #059669;
                    padding: 6px 14px;
                    border-radius: 10px;
                    font-weight: 900;
                    font-size: 1.1rem;
                    border: 1px solid #a7f3d0;
                }

                .wbc-price-val-box {
                    display: flex;
                    align-items: baseline;
                    gap: 4px;
                }

                .wbc-price-amount {
                    font-size: 1.6rem;
                    font-weight: 900;
                    color: #0f172a;
                    line-height: 1;
                }

                .wbc-price-curr {
                    font-size: 0.95rem;
                    font-weight: 800;
                    color: #64748b;
                }

                .wbc-old-price {
                    font-size: 0.9rem;
                    font-weight: 700;
                    color: #94a3b8;
                    text-decoration: line-through;
                    margin-right: 6px;
                }

                .wbc-btn-view {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 14px;
                    color: #1e293b;
                    font-family: inherit;
                    font-weight: 900;
                    font-size: 0.95rem;
                    text-decoration: none;
                    transition: all 0.25s ease;
                }

                .wbc-card:hover .wbc-btn-view {
                    background: #6366f1;
                    color: #ffffff;
                    border-color: #6366f1;
                    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.28);
                }

                /* ── Pagination & Loader ── */
                .wbc-pagination-wrap {
                    display: flex;
                    justify-content: center;
                    margin-top: 60px;
                    width: 100%;
                    overflow-x: auto;
                }

                .wbc-loader-wrap {
                    min-height: 45vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* ── Empty State ── */
                .wbc-empty-state {
                    text-align: center;
                    padding: 80px 20px;
                    background: #ffffff;
                    border-radius: 28px;
                    border: 1.5px dashed #cbd5e1;
                    max-width: 600px;
                    margin: 40px auto;
                }

                .wbc-empty-icon {
                    width: 80px;
                    height: 80px;
                    margin: 0 auto 20px;
                    background: #eef2ff;
                    color: #6366f1;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                }

                .wbc-empty-state h3 {
                    font-size: 1.6rem;
                    font-weight: 900;
                    color: #0f172a;
                    margin: 0 0 12px;
                }

                .wbc-empty-state p {
                    font-size: 1.1rem;
                    color: #64748b;
                    margin: 0 0 28px;
                    line-height: 1.7;
                }

                .wbc-btn-reset-empty {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 32px;
                    background: #6366f1;
                    color: #ffffff;
                    border: none;
                    border-radius: 50px;
                    font-family: inherit;
                    font-weight: 800;
                    font-size: 1.05rem;
                    cursor: pointer;
                    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
                    transition: all 0.25s ease;
                }

                .wbc-btn-reset-empty:hover {
                    background: #4f46e5;
                    transform: translateY(-2px);
                }

                /* ── Responsive Media Queries ── */
                @media (max-width: 992px) {
                    .wbc-hero-controls {
                        flex-wrap: wrap;
                        gap: 12px;
                    }
                    .wbc-search-box {
                        flex: 1 1 100%;
                    }
                    .wbc-hero-dropdown-item {
                        flex: 1 1 calc(50% - 6px);
                        min-width: 150px;
                    }
                    .wbc-hero-dropdown-item.is-compact {
                        flex: 1 1 calc(50% - 6px);
                        min-width: 150px;
                    }
                    .wbc-grid {
                        grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
                        gap: 22px;
                    }
                }

                @media (max-width: 768px) {
                    .wbc-container {
                        padding: 0 16px;
                    }
                    .wbc-hero {
                        padding: 110px 0 24px;
                    }
                    .wbc-title {
                        font-size: 1.95rem;
                    }
                    .wbc-subtitle {
                        font-size: 1rem;
                    }
                    .wbc-main-layout {
                        padding: 24px 0 70px;
                    }
                    .wbc-hero-controls {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 10px;
                        margin-bottom: 16px;
                    }
                    .wbc-search-box input {
                        height: 52px;
                        font-size: 0.98rem;
                        padding: 0 48px 0 110px;
                    }
                    .wbc-search-icon {
                        right: 16px;
                        font-size: 1.05rem;
                    }
                    .wbc-search-submit-btn {
                        height: 40px;
                        padding: 0 16px;
                        font-size: 0.88rem;
                        left: 6px;
                    }
                    .wbc-search-clear {
                        left: 84px;
                    }
                    .wbc-hero-dropdown-item,
                    .wbc-hero-dropdown-item.is-compact {
                        width: 100%;
                        flex: 1 1 100%;
                    }
                    .wbc-dropdown-trigger-hero {
                        height: 52px;
                        width: 100%;
                        padding: 0 16px;
                    }
                    .wbc-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 12px;
                        padding: 16px;
                        margin-bottom: 18px;
                    }
                    .wbc-results-count {
                        font-size: 0.98rem;
                        text-align: center;
                    }
                    .wbc-toolbar-right {
                        flex-direction: column;
                        align-items: stretch;
                        width: 100%;
                        gap: 10px;
                    }
                    .wbc-toolbar-dropdown-wrap {
                        width: 100%;
                    }
                    .wbc-dropdown-trigger-toolbar {
                        width: 100%;
                        height: 48px;
                    }
                    .wbc-btn-filter-toggle {
                        width: 100%;
                        height: 48px;
                        justify-content: center;
                    }
                    .wbc-filter-panel-inner {
                        padding: 18px 16px;
                        border-radius: 18px;
                        gap: 16px;
                    }
                    .wbc-custom-price-inputs {
                        max-width: 100%;
                        width: 100%;
                        gap: 10px;
                    }
                    .wbc-price-input-wrapper input {
                        height: 44px;
                        font-size: 0.92rem;
                        padding: 0 12px 0 40px;
                    }
                    .wbc-price-separator {
                        padding-bottom: 10px;
                        font-size: 0.9rem;
                    }
                    .wbc-filter-actions {
                        flex-direction: column-reverse;
                        width: 100%;
                        gap: 8px;
                    }
                    .wbc-btn-reset,
                    .wbc-btn-close-filter {
                        width: 100%;
                        justify-content: center;
                        padding: 10px 16px;
                    }
                    .wbc-grid {
                        grid-template-columns: 1fr;
                        gap: 18px;
                    }
                    .wbc-card {
                        border-radius: 22px;
                    }
                    .wbc-card-media {
                        height: 200px;
                    }
                    .wbc-card-body {
                        padding: 18px;
                    }
                    .wbc-course-name {
                        font-size: 1.22rem;
                    }
                    .wbc-pagination-wrap {
                        margin-top: 40px;
                    }
                }

                @media (max-width: 480px) {
                    .wbc-container {
                        padding: 0 12px;
                    }
                    .wbc-title {
                        font-size: 1.65rem;
                    }
                    .wbc-search-box input {
                        height: 48px;
                        font-size: 0.92rem;
                        padding: 0 42px 0 95px;
                    }
                    .wbc-search-icon {
                        right: 14px;
                        font-size: 0.95rem;
                    }
                    .wbc-search-submit-btn {
                        height: 36px;
                        padding: 0 12px;
                        font-size: 0.82rem;
                        left: 6px;
                    }
                    .wbc-search-clear {
                        left: 72px;
                        width: 22px;
                        height: 22px;
                        font-size: 0.75rem;
                    }
                    .wbc-dropdown-trigger-hero {
                        height: 48px;
                        padding: 0 14px;
                    }
                    .wbc-trigger-val {
                        max-width: 150px;
                        font-size: 0.92rem;
                    }
                    .wbc-dropdown-trigger-toolbar,
                    .wbc-btn-filter-toggle {
                        height: 44px;
                    }
                    .wbc-custom-price-inputs {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 10px;
                    }
                    .wbc-price-separator {
                        display: none;
                    }
                    .wbc-price-input-group {
                        width: 100%;
                    }
                    .wbc-card-media {
                        height: 180px;
                    }
                    .wbc-card-body {
                        padding: 14px;
                    }
                    .wbc-course-name {
                        font-size: 1.12rem;
                    }
                    .wbc-price-amount {
                        font-size: 1.35rem;
                    }
                    .wbc-btn-view {
                        padding: 8px 14px;
                        font-size: 0.88rem;
                    }
                    .wbc-stat-badge {
                        font-size: 0.76rem;
                        padding: 4px 8px;
                    }
                }
            `}</style>
        </div>
    );
};

export default WebsiteCourses;
